import {
  LTLQuickQuoteRequest,
  LTLQuickQuoteStop,
  getLTLQuickQuoteResult,
  getLTLQuickQuotes,
} from 'lib/api/getLTLQuickQuotes';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import {
  getLtlQuotePollIntervalMs,
  getLtlQuotePollMaxAttempts,
} from '../constants';
import {
  LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
  getLtlProviderOutcomeConfig,
} from '../ltlProviderOutcomeConfig';
import { waitForDocumentVisible, waitWithSignal } from './abortableWait';
import {
  ClassifiedLTLQuoteResult,
  classifyLTLQuoteResult,
} from './classifyLTLQuoteResult';

export enum LTLQuoteJobStatus {
  Rated = 'rated',
  Failed = 'failed',
  // A resumed job the server no longer has.
  Unknown = 'unknown',
}

export enum LTLQuoteJobFailureLevel {
  Error = 'error',
  Warning = 'warning',
}

// Where a job is on the server; enough to read its result again later.
export type LTLQuoteJobPointer = {
  jobId: string;
  startedAt: number;
  stops: LTLQuickQuoteStop[];
};

export type LTLQuoteJobOutcome =
  | (LTLQuoteJobPointer & {
      status: LTLQuoteJobStatus.Rated;
      result: ClassifiedLTLQuoteResult;
    })
  | {
      status: LTLQuoteJobStatus.Failed;
      pointer: Maybe<LTLQuoteJobPointer>;
      failureMessage: string;
      level: LTLQuoteJobFailureLevel;
      // The TMS's polling window ran out before the job finished.
      isTimeout: boolean;
    }
  | { status: LTLQuoteJobStatus.Unknown };

type LTLQuoteJobOptions = {
  tms: Undef<TMS>;
  isThreeGActive: boolean;
  signal: AbortSignal;
};

type StartLTLQuoteJobOptions = LTLQuoteJobOptions & {
  // Called once the server accepts the job, before polling, so callers can keep the pointer
  // and the quote request the server resolved (a new one when the request sent 0).
  onJobStarted?: (pointer: LTLQuoteJobPointer, quoteRequestId: number) => void;
};

type PollLTLQuoteJobOptions = LTLQuoteJobOptions & {
  // Resumed jobs read the result right away; new jobs wait for the first interval.
  pollImmediately: boolean;
};

// Starts an LTL quick quote job and polls it until the TMS answers or its polling window
// ends. Shared by single and batch LTL so the polling rules live in one place.
export async function runLTLQuoteJob(
  request: LTLQuickQuoteRequest,
  { onJobStarted, ...options }: StartLTLQuoteJobOptions
): Promise<LTLQuoteJobOutcome> {
  const startResult = await getLTLQuickQuotes(request);
  throwIfAborted(options.signal);

  if (startResult.isErr()) {
    return {
      status: LTLQuoteJobStatus.Failed,
      pointer: null,
      failureMessage:
        startResult.error.message || LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
      level: toFailureLevel(startResult.error.level),
      isTimeout: false,
    };
  }

  const pointer: LTLQuoteJobPointer = {
    jobId: startResult.value.jobId,
    startedAt: Date.now(),
    stops: request.stops,
  };
  onJobStarted?.(
    pointer,
    startResult.value.quoteRequestId ?? request.quoteRequestId
  );

  return pollLTLQuoteJob(pointer, { ...options, pollImmediately: false });
}

// Polls a job until it finishes or the TMS's polling window (counted from when the job
// started) runs out.
export async function pollLTLQuoteJob(
  pointer: LTLQuoteJobPointer,
  { tms, isThreeGActive, signal, pollImmediately }: PollLTLQuoteJobOptions
): Promise<LTLQuoteJobOutcome> {
  const { jobId, stops, startedAt } = pointer;
  const outcomeConfig = getLtlProviderOutcomeConfig(tms);
  const maxAttempts = getLtlQuotePollMaxAttempts(tms);
  const deadline = startedAt + getLTLQuotePollWindowMs(tms);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (!pollImmediately || attempt > 0) {
      await waitWithSignal(getLtlQuotePollIntervalMs(attempt, tms), signal);
    }
    await waitForDocumentVisible(signal);

    const result = await getLTLQuickQuoteResult({ jobId, stops });
    throwIfAborted(signal);

    if (result.isOk()) {
      return {
        ...pointer,
        status: LTLQuoteJobStatus.Rated,
        result: classifyLTLQuoteResult(result.value, {
          isThreeGActive,
          outcomeConfig,
        }),
      };
    }

    const { status } = result.error;
    if (pollImmediately && status === 404) {
      return { status: LTLQuoteJobStatus.Unknown };
    }

    const isStillProcessing = status === 202 || status === 404;
    if (!isStillProcessing) {
      return {
        status: LTLQuoteJobStatus.Failed,
        pointer,
        failureMessage:
          result.error.message || LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
        level: toFailureLevel(result.error.level),
        isTimeout: false,
      };
    }

    if (Date.now() >= deadline) {
      break;
    }
  }

  return {
    status: LTLQuoteJobStatus.Failed,
    pointer,
    failureMessage:
      outcomeConfig?.systemFailureMessage ?? LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
    level: LTLQuoteJobFailureLevel.Error,
    isTimeout: true,
  };
}

// How long a job is polled for this TMS.
export function getLTLQuotePollWindowMs(tms: Undef<TMS>): number {
  return Array.from({ length: getLtlQuotePollMaxAttempts(tms) }, (_, attempt) =>
    getLtlQuotePollIntervalMs(attempt, tms)
  ).reduce((total, interval) => total + interval, 0);
}

function toFailureLevel(level: Undef<string>): LTLQuoteJobFailureLevel {
  return level === LTLQuoteJobFailureLevel.Warning
    ? LTLQuoteJobFailureLevel.Warning
    : LTLQuoteJobFailureLevel.Error;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}
